import type {AnimationEvent, ReactElement, ReactPortal} from 'react';
import {useEffect, useId, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {ClickableDiv} from '@Components/clickable-div';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text/text';
import {focusPromptBox} from '@Components/ai-chat-window/lib/ai-chat-prompt.library';
import {openPricingWithPromo} from '@Modals/premium-pricing-modal/premium-pricing-modal.library';
import {NewSubscriptionStartSource} from '@Libraries/ga-events';
import {useAiChat} from '@AiChat/providers/ai-chat.context';
import {isAiChatMotionReduced} from '@AiChat/lib/ai-chat-motion.library';
import type {CssVariableStyle} from '@AiChat/lib/ai-chat-style.types';
import {KEYBOARD_KEY} from '@Utils/dom.util';
import {useIsSmallerThanBreakpointWidth} from '@Hooks/useIsSmallerThanBreakpoint';
import {ScreenBreakpoints} from '@Libraries/responsiveness-library';
import type {AiChatChristmasGiftUnlockProps, AiChatChristmasUnlockSpark} from './ai-chat-christmas-gift-unlock.types';
import {AI_CHAT_CHRISTMAS_UNLOCK_SPARKS, GIFT_UNLOCK_SHOW_MS} from './ai-chat-christmas-gift-unlock.types';
import styles from './ai-chat-christmas-gift-unlock.module.scss';

export function AiChatChristmasGiftUnlock({onClose}: AiChatChristmasGiftUnlockProps): ReactPortal | null {
  const {state} = useAiChat();
  const offer = state.christmasGiftOffer;
  const [isLeaving, setIsLeaving] = useState<boolean>(false);
  const unlockRef = useRef<HTMLDivElement>(null);
  const kickerId = useId();
  const isCompact = useIsSmallerThanBreakpointWidth(ScreenBreakpoints.MEDIUM);

  useEffect(() => {
    unlockRef.current?.focus({preventScroll: true});
    const showTimer = window.setTimeout(() => {
      setIsLeaving(true);
    }, GIFT_UNLOCK_SHOW_MS);

    return (): void => {
      window.clearTimeout(showTimer);
    };
  }, []);

  useEffect(() => {
    if (!isLeaving || !isAiChatMotionReduced()) {
      return;
    }

    openPlansAfterUnlock();
  }, [isLeaving]);

  useEffect(() => {
    const onDocumentKeyDown = (event: KeyboardEvent): void => {
      if (event.key === KEYBOARD_KEY.ESCAPE) {
        closeUnlock();
      }
    };

    document.addEventListener('keydown', onDocumentKeyDown);

    return (): void => {
      document.removeEventListener('keydown', onDocumentKeyDown);
    };
  }, []);

  const closeUnlock = (): void => {
    focusPromptBox(state.promptBoxId);
    onClose();
  };

  const openPlansAfterUnlock = (): void => {
    closeUnlock();
    if (offer !== null) {
      openPricingWithPromo(NewSubscriptionStartSource.AI_CHAT_CHRISTMAS_GIFT, offer.promoCode);
    }
  };

  const onUnlockClick = (): void => {
    setIsLeaving(true);
  };

  const onUnlockAnimationEnd = (event: AnimationEvent<HTMLDivElement>): void => {
    if (event.target !== event.currentTarget) {
      return;
    }

    openPlansAfterUnlock();
  };

  const getOverlayClassName = (): string => {
    if (isCompact) {
      return `${styles.overlay} ${styles.compact}`;
    }

    return styles.overlay;
  };

  const getUnlockClassName = (): string => {
    if (isLeaving) {
      return `${styles.unlock} ${styles.isLeaving}`;
    }

    return styles.unlock;
  };

  const getSpark = (spark: AiChatChristmasUnlockSpark): ReactElement => {
    return <span key={spark.angleDeg} className={styles.spark} style={getSparkStyle(spark)} />;
  };

  const getSparkStyle = (spark: AiChatChristmasUnlockSpark): CssVariableStyle => {
    return {
      '--spark-angle': `${spark.angleDeg}deg`,
      '--spark-reach': `${-spark.reachPx}px`,
      '--spark-size': `${spark.sizePx}px`,
    };
  };

  if (offer === null) {
    return null;
  }

  return createPortal(
    <div className={getOverlayClassName()} role="dialog" aria-modal="true" aria-labelledby={kickerId}>
      <div className={styles.backdrop} />
      <ClickableDiv ref={unlockRef} className={getUnlockClassName()} tabIndex={-1} enableFocusOutline={false} onClick={onUnlockClick} onAnimationEnd={onUnlockAnimationEnd}>
        <p id={kickerId} className={styles.kicker}>
          {window.i18next.t('pmwjs_ai_chat_christmas_gift_unlocked')}
        </p>
        <div className={styles.ticket}>
          {AI_CHAT_CHRISTMAS_UNLOCK_SPARKS.map(getSpark)}
          <div className={styles.ticketFace}>
            <div className={styles.ticketTop}>
              <Text className={styles.discount} val={window.i18next.t('pmwjs_x_off', {discount: window.PMW.util.percentageFormat(offer.discountFraction)})} />
              <Text className={styles.plans} size={TextSize.LG} bold val={window.i18next.t('pmwjs_ai_chat_christmas_gift_premium_plans')} />
            </div>
            <div className={styles.ticketStub}>
              <span className={styles.code}>{offer.promoCode}</span>
            </div>
          </div>
        </div>
        <div className={styles.codeHint}>
          <Icon icon="icon-check" size={IconSize.SIZE_ICON_16} type={IconType.NONE} shape={IconShape.NONE} />
          <Text size={TextSize.MD} bold val={window.i18next.t('pmwjs_ai_chat_christmas_gift_use_code_at_checkout', {code: offer.promoCode})} />
        </div>
        <Text className={styles.choosePlan} size={TextSize.MD} val={window.i18next.t('pmwjs_ai_chat_christmas_gift_choose_a_plan')} />
      </ClickableDiv>
    </div>,
    document.body
  );
}
